import {
  ApolloClient,
  createQueryPreloader,
  FetchResult,
  from,
  InMemoryCache,
  NormalizedCacheObject,
  Observable,
  Operation,
  PreloadQueryFunction,
  split,
} from "@apollo/client";
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { getMainDefinition } from "@apollo/client/utilities";
import createUploadLink from "apollo-upload-client/createUploadLink.mjs";
import { getCurrentLang } from "@/features/language";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { GraphQLFormattedError } from "graphql/error";
import { Kind, OperationTypeNode } from "graphql";
import { router } from "@/shared/lib/router";
import { AUTH_ROUTE, SHARE_ROUTE } from "@/shared/lib/routes";
import { csrfRequest, getXsrfToken } from "@/shared/lib/csrf";
import fragmentMatcherJson from "./fragment-matcher.json";
import { typePolicies } from "@/shared/gql/typePolicies";
import { create } from "zustand";
import { openUpgradeDialog } from "@/shared/lib/upgrade-dialog";
import { openOutOfCreditsDialog } from "@/shared/lib/out-of-credits-dialog";
import { graphqlActivityLink } from "@/shared/gql/graphql-activity";
import { trackCreditLimitHit } from "@/shared/lib/analytics";

const isAuthError = (e: GraphQLFormattedError) =>
  e.message === "Unauthenticated.";

// Matched on the code: the message is translated, so it differs per locale.
const isCreditLimitError = (e: GraphQLFormattedError) =>
  e.extensions?.code === "credit_limit_reached";

const isQuery = (operation: Operation) => {
  const definition = getMainDefinition(operation.query);
  return (
    definition.kind === Kind.OPERATION_DEFINITION &&
    definition.operation === OperationTypeNode.QUERY
  );
};

const isSubscriptionRequiredError = (e: GraphQLFormattedError) =>
  e.message.startsWith("subscription_required:");

const isValidationError = (e: GraphQLFormattedError) =>
  !!e.extensions?.validation;

// Queries are safe to batch into one HTTP request and are what the page-load
// waterfall fires many of at once. Mutations are not batched: every file upload
// (Upload scalar) is a mutation and must go through the multipart upload link,
// which cannot be batched. A query that waits on a third party, or a slow
// aggregate such as a page's count tiles, opts out with UNBATCHED_CONTEXT, since
// a batch answers only once its slowest query does.
export const UNBATCHED_CONTEXT = { unbatched: true };

export const isBatchableOperation = (operation: Operation): boolean =>
  isQuery(operation) && !operation.getContext().unbatched;

const redirectToAuthIfNeeded = () => {
  const currentPath = router.state.location.pathname;

  // Skip navigation if already on auth pages (login, signup, join, etc.)
  if (currentPath.startsWith(AUTH_ROUTE)) {
    return;
  }

  // Skip navigation on public routes — anonymous visitors are expected here
  // and should not be bounced to login when the optimistic `me` probe 401s.
  if (currentPath.startsWith(SHARE_ROUTE)) {
    return;
  }

  router.navigate({
    to: AUTH_ROUTE,
    search: currentPath ? { redirect: currentPath } : {},
    replace: true,
  });
  resetClient();
};

const extractFeatureFromError = (e: GraphQLFormattedError): string =>
  e.message.replace("subscription_required:", "");

const showUpgradeDialog = (feature: string) => {
  const currentPath = router.state.location.pathname;
  const companySlugMatch = currentPath.match(/^\/([^/]+)/);

  if (companySlugMatch) {
    openUpgradeDialog(feature, companySlugMatch[1]);
  }
};

// A detail page whose object is locked: /{companySlug}/{section}/{id}.
const LOCKED_DETAIL_PATTERN =
  /^\/([^/]+)\/(tenders|organisations|organizations|caselaw|horizon)\/[^/]+/;

/** The object does not open: leave its detail page for the list it came from. */
const navigateBackToList = () => {
  const match = router.state.location.pathname.match(LOCKED_DETAIL_PATTERN);

  if (match) {
    router.navigate({ href: `/${match[1]}/${match[2]}`, replace: true });
  }
};

export const createCache = () =>
  new InMemoryCache({
    possibleTypes: fragmentMatcherJson.possibleTypes,
    typePolicies,
  });

/**
 * Creates a new Apollo client instance.
 * This can be called multiple times, it will create a new client each time.
 * This is useful for when you want to forget about the cache, (for example by an unauth error or logout)
 */
export const createApolloClient = () => {
  // Log any GraphQL errors, protocol errors, or network error that occurred
  const errorLink = onError(
    ({ graphQLErrors, networkError, operation, forward }) => {
      // Tracking only: it leaves the handling below unchanged.
      trackCreditLimitHit(graphQLErrors, () => router.state.location.pathname);

      // Handle authentication errors
      if (graphQLErrors?.some(isAuthError)) {
        redirectToAuthIfNeeded();
        return;
      }

      // Opening a locked object with no data credits left. A mutation that
      // runs out (an export, a CRM sync) shows its own message instead.
      if (graphQLErrors?.some(isCreditLimitError) && isQuery(operation)) {
        openOutOfCreditsDialog("data", null);
        navigateBackToList();
        return;
      }

      // Handle subscription required errors
      if (graphQLErrors?.some(isSubscriptionRequiredError)) {
        const subscriptionError = graphQLErrors.find(
          isSubscriptionRequiredError,
        );
        if (subscriptionError) {
          showUpgradeDialog(extractFeatureFromError(subscriptionError));
        }
        return;
      }

      // Handle validation errors — these are expected and handled by forms/toasts
      if (graphQLErrors?.every(isValidationError)) {
        return;
      }

      // Handle CSRF mismatch (419). Stale XSRF cookies — from a previous
      // session evicted server-side, an APP_KEY rotation, or a cookie written
      // by a different env that shares the SESSION_DOMAIN — cause every request
      // to fail until the cookie is refreshed. Re-fetch /sanctum/csrf-cookie
      // and retry the operation once before falling back to /auth.
      if (
        networkError &&
        "statusCode" in networkError &&
        networkError.statusCode === 419
      ) {
        if (operation.getContext().csrfRetried) {
          redirectToAuthIfNeeded();
          return;
        }
        operation.setContext({ csrfRetried: true });

        return new Observable<FetchResult>((observer) => {
          csrfRequest()
            .then(() => {
              forward(operation).subscribe({
                next: observer.next.bind(observer),
                error: observer.error.bind(observer),
                complete: observer.complete.bind(observer),
              });
            })
            .catch((err) => observer.error(err));
        });
      }
    },
  );

  const uri = import.meta.env.VITE_BACKEND_URL + "/graphql";

  const httpOptions = {
    uri,
    credentials: "include",
    headers: {
      "X-Requested-With": "XMLHttpRequest",
    },
  };

  // Uploads speak multipart and can't be batched; everything else (queries) is
  // collapsed into one HTTP request per tick to cut the page-load round trips.
  const uploadLink = createUploadLink(httpOptions);
  const batchLink = new BatchHttpLink({
    ...httpOptions,
    batchMax: 15,
    batchInterval: 20,
  });

  const httpLink = split(isBatchableOperation, batchLink, uploadLink);

  const authLink = setContext((_, { headers }) => {
    const csrfToken = getXsrfToken();

    return {
      headers: {
        ...headers,
        ...(csrfToken ? { "X-XSRF-TOKEN": csrfToken } : {}),
        "Accept-Language": getCurrentLang(),
      },
    };
  });

  return new ApolloClient({
    devtools: {
      enabled: import.meta.env.DEV,
    },
    cache: createCache(),
    defaultOptions: {
      watchQuery: {
        fetchPolicy: "cache-first",
      },
    },
    link: from([graphqlActivityLink, errorLink, authLink, httpLink]),
  });
};

interface ClientStore {
  client: ApolloClient<NormalizedCacheObject>;
  preloadQuery: PreloadQueryFunction;
}

export const useClientStore = create<ClientStore>(() => {
  const client = createApolloClient();
  return {
    client,
    preloadQuery: createQueryPreloader(client),
  };
});

// We do not export this directly, otherwise old clients will still be referenced
export const getClient = () => {
  const client = useClientStore.getState().client;
  if (!client) throw new Error("Apollo client not initialized");
  return client;
};

export const getPreloadQuery = () => {
  const preloadQuery = useClientStore.getState().preloadQuery;
  if (!preloadQuery) throw new Error("Apollo preloadQuery not initialized");
  return preloadQuery;
};

// Which slice of data the cache currently holds. Module scope rather than React state:
// resetClient() swaps the client, which remounts everything under ApolloProvider, and a ref
// or state would be re-initialized by that remount.
let cacheScope: string | null = null;

export const resetClient = () => {
  // Logout and the 401 path land here too. Forgetting the scope makes the next one read as a
  // first landing rather than a change, so they don't reset a cache they just rebuilt.
  cacheScope = null;
  const client = createApolloClient();
  useClientStore.setState({
    client,
    preloadQuery: createQueryPreloader(client),
  });
};

/**
 * Drops the cache when the caller declares a different scope than it currently holds.
 *
 * Company-scoped data is cached under keys that carry no company. `currentUser(company:)`
 * returns the authenticated user, whose id is the same everywhere, while its company, role,
 * status, teams, views and bookmarks all come from the injected company, so both companies
 * normalize onto one entry and whichever loaded last wins. Keeping the cache across a switch
 * therefore serves the previous company's data until the user reloads by hand.
 */
export const resetCacheOnScopeChange = (scope: string) => {
  if (cacheScope !== null && cacheScope !== scope) {
    resetClient();
  }

  cacheScope = scope;
};
